"use strict";
/**
 * Events Page Handler
 * Manages the event list, search, and filtering
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.loadEvents = loadEvents;
exports.setupEventsPage = setupEventsPage;
const state_1 = require("../state");
const ui_1 = require("../ui");
const markets_1 = require("../markets");
const const_1 = require("../const");
const api_1 = require("../api");
const operator_error_1 = require("../api/operator-error");
const data_source_service_1 = require("../services/data-source-service");
const market_selection_service_1 = require("../services/market-selection-service");
const pill_selector_1 = require("../ui/pill-selector");
const capabilities_1 = require("../venues/capabilities");
const venues_1 = require("../../shared/venues");
const navigation_service_1 = require("../services/navigation-service");
/**
 * Compose the full Kalshi series ticker from the prefix and suffix selectors
 * (e.g. `KXMLB` + `GAME` = `KXMLBGAME`). Returns `null` when either selector is
 * empty or still sitting on an unresolved "➕ Custom…" sentinel.
 */
function getKalshiCombinedCategory(prefixSelector, suffixSelector) {
    const prefix = prefixSelector?.value;
    const suffix = suffixSelector?.value;
    if (!prefix || !suffix)
        return null;
    if (prefix === data_source_service_1.CUSTOM_KALSHI_PREFIX_VALUE ||
        suffix === data_source_service_1.CUSTOM_KALSHI_SUFFIX_VALUE) {
        return null;
    }
    return `${prefix}${suffix}`;
}
/**
 * Prompt for a custom prefix/suffix, persist it, add/select a matching option,
 * and return the normalized value. Returns `null` if the user cancels or enters
 * nothing, reverting the selector to its previous value.
 */
function resolveCustomEntry(selector, sentinelValue, config) {
    const rawInput = window.prompt(config.promptMessage, "");
    const trimmed = rawInput?.trim().toUpperCase() || "";
    if (!trimmed) {
        selector.value =
            selector.dataset.lastValue || selector.options[0]?.value || "";
        return null;
    }
    config.persist(trimmed);
    let option = selector.querySelector(`option[value="${CSS.escape(trimmed)}"]`);
    if (!option) {
        option = document.createElement("option");
        option.dataset.customValue = "true";
        option.value = trimmed;
        option.textContent = `Custom: ${trimmed}`;
        const sentinel = selector.querySelector(`option[value='${sentinelValue}']`);
        if (sentinel) {
            selector.insertBefore(option, sentinel);
        }
        else {
            selector.appendChild(option);
        }
    }
    selector.value = trimmed;
    selector.dataset.lastValue = trimmed;
    return trimmed;
}
const resolveCustomPrefix = (selector) => resolveCustomEntry(selector, data_source_service_1.CUSTOM_KALSHI_PREFIX_VALUE, {
    promptMessage: "Enter a Kalshi league prefix (e.g., KXNCAAMBB)",
    persist: state_1.saveCustomPrefix,
});
const resolveCustomSuffix = (selector) => resolveCustomEntry(selector, data_source_service_1.CUSTOM_KALSHI_SUFFIX_VALUE, {
    promptMessage: "Enter a Kalshi market suffix (e.g., 2HWINNER)",
    persist: state_1.saveCustomSuffix,
});
/**
 * Load events from API with optional league filter
 * If no league is provided, uses the current selector values
 * or defaults based on data source
 */
async function loadEvents(league, pmTagSlug) {
    try {
        console.log("[loadEvents] Starting market load");
        console.log("  - League/sport param:", league);
        console.log("  - PM tag slug:", pmTagSlug);
        (0, ui_1.showEventsLoading)(true);
        // If no league provided, derive it from the current selectors.
        let categoryToUse = league;
        if (!categoryToUse) {
            const state = (0, state_1.getState)();
            if ((0, capabilities_1.usesSeriesSelector)(state.dataSource)) {
                const prefixSelector = document.getElementById("market-category");
                const suffixSelector = document.getElementById("market-suffix");
                categoryToUse =
                    getKalshiCombinedCategory(prefixSelector, suffixSelector) ??
                        const_1.LEAGUES.NBA;
                console.log("[loadEvents] Kalshi combined category:", categoryToUse);
            }
            else {
                const categorySelector = document.getElementById("market-category");
                categoryToUse = categorySelector?.value || "";
                console.log("[loadEvents] Got category from selector:", categoryToUse);
            }
        }
        console.log("[loadEvents] Fetching markets with category:", categoryToUse);
        const events = await (0, markets_1.fetchMarkets)(categoryToUse, pmTagSlug);
        console.log("[loadEvents] Fetched", events.length, "events");
        // Apply existing search filter if the search input has a value
        const eventSearch = document.getElementById("event-search");
        const searchTerm = eventSearch?.value?.trim().toLowerCase() || "";
        const eventsToRender = searchTerm
            ? (0, markets_1.searchDisplayEvents)(searchTerm, events, (0, state_1.getState)().dataSource)
            : events;
        (0, ui_1.renderEnhancedEventsList)(eventsToRender);
        (0, ui_1.showEventsLoading)(false);
    }
    catch (error) {
        console.error("[loadEvents] Failed to load events:", error);
        (0, ui_1.showEventsLoading)(false);
        // Check if error is due to invalid secret key
        if ((0, api_1.isSecretKeyError)(error)) {
            (0, ui_1.showReAuthModal)();
            (0, ui_1.showError)("Invalid secret key. Please re-enter it.", "market-error");
        }
        else {
            (0, ui_1.showError)((0, operator_error_1.operatorMessage)(error, "Failed to load events. Check your API credentials."), "market-error");
        }
    }
}
/**
 * Setup events page event listeners
 */
function setupEventsPage() {
    const settingsBtn = document.getElementById("settings-btn-events");
    const eventSearch = document.getElementById("event-search");
    const refreshBtn = document.getElementById("refresh-events-btn");
    const openPairBuilderBtn = document.getElementById("open-pair-builder-btn");
    const openMonitorStatusBtn = document.getElementById("open-monitor-status-btn");
    const openPnlBtn = document.getElementById("open-pnl-btn");
    const openWinProbBtn = document.getElementById("open-win-prob-btn");
    // In Kalshi mode this holds the league prefix; in Polymarket modes the sport.
    const prefixSelector = document.getElementById("market-category");
    // Kalshi-only market-type suffix selector.
    const suffixSelector = document.getElementById("market-suffix");
    const dataSourcePills = (0, pill_selector_1.initPillSelector)(document.getElementById("data-source"));
    const dismissBtn = document.getElementById("dismiss-market-error");
    const favoriteBtn = document.getElementById("favorite-league-btn");
    const removePrefixBtn = document.getElementById("remove-custom-prefix-btn");
    const removeSuffixBtn = document.getElementById("remove-custom-suffix-btn");
    /** Show or hide the Kalshi-only suffix selector (unused for Polymarket venues). */
    function setKalshiSuffixVisible(visible) {
        if (suffixSelector)
            suffixSelector.style.display = visible ? "" : "none";
    }
    /** Show each remove-custom button only when its selector holds a custom entry (Kalshi only). */
    function updateRemoveBtnsVisibility() {
        const isKalshi = (0, capabilities_1.usesSeriesSelector)((0, state_1.getState)().dataSource);
        if (removePrefixBtn) {
            const opt = prefixSelector?.selectedOptions[0];
            removePrefixBtn.style.display =
                isKalshi && opt?.dataset.customValue === "true" ? "" : "none";
        }
        if (removeSuffixBtn) {
            const opt = suffixSelector?.selectedOptions[0];
            removeSuffixBtn.style.display =
                isKalshi && opt?.dataset.customValue === "true" ? "" : "none";
        }
    }
    /** Flash the favorite button to confirm a save. */
    function flashFavoriteSaved() {
        if (!favoriteBtn)
            return;
        favoriteBtn.textContent = "✅";
        setTimeout(() => {
            favoriteBtn.textContent = "⭐";
        }, 1500);
    }
    if (dismissBtn) {
        dismissBtn.addEventListener("click", () => (0, ui_1.hideError)("market-error"));
    }
    // Initialize selectors based on current data source BEFORE setting up listeners
    const currentState = (0, state_1.getState)();
    const usesFlatLeagueList = !(0, capabilities_1.usesSeriesSelector)(currentState.dataSource);
    console.log("[setupEventsPage] Initializing events page");
    console.log("  - Data source:", currentState.dataSource);
    console.log("  - Is authenticated:", currentState.isAuthenticated);
    console.log("  - Session token exists:", !!currentState.sessionToken);
    // Sync the pills to the stored data source (no reload — nothing loaded yet).
    dataSourcePills?.setValue(currentState.dataSource);
    // Immediately clear and set correct selector options based on data source
    // This prevents showing wrong leagues (e.g., Kalshi leagues when Polymarket is selected)
    if (usesFlatLeagueList && prefixSelector) {
        console.log(`[setupEventsPage] ${currentState.dataSource} mode detected`);
        // Kalshi suffix selector is not used for Polymarket venues.
        setKalshiSuffixVisible(false);
        // Clear Kalshi options from HTML and show loading state
        prefixSelector.innerHTML =
            '<option value="">Loading sports...</option>';
        prefixSelector.disabled = true;
        // Only fetch sports if authenticated (API requires session token)
        if (currentState.isAuthenticated) {
            console.log(`[setupEventsPage] User is authenticated, fetching ${currentState.dataSource} sports...`);
            // Start fetching the venue's leagues, then load markets
            (0, data_source_service_1.populateFlatLeagueSelector)(prefixSelector, currentState.dataSource)
                .then(() => {
                console.log("[setupEventsPage] Sports populated successfully");
                // After sports are populated, load markets
                const firstSport = prefixSelector?.value;
                console.log("[setupEventsPage] First sport selected:", firstSport);
                if (firstSport) {
                    console.log("[setupEventsPage] Loading markets for sport:", firstSport);
                    loadEvents(firstSport);
                }
                else {
                    console.warn("[setupEventsPage] No sport selected after population");
                }
            })
                .catch((error) => {
                console.error(`[setupEventsPage] Failed to populate ${currentState.dataSource} sports:`, error);
            });
        }
        else {
            console.warn("[setupEventsPage] User NOT authenticated, skipping sports fetch");
            console.log("[setupEventsPage] Selector will show 'Loading sports...' until authenticated");
        }
    }
    else if (prefixSelector) {
        console.log("[setupEventsPage] Kalshi mode detected, populating prefixes + suffixes");
        // For Kalshi, populate the prefix selector then the league-specific suffixes.
        (0, data_source_service_1.populateKalshiPrefixes)(prefixSelector);
        (0, data_source_service_1.populateKalshiSuffixes)(suffixSelector, prefixSelector?.value ?? null);
        setKalshiSuffixVisible(true);
    }
    // Data source change: swap venue, then reload the list for it.
    dataSourcePills?.onChange(async (newSource) => {
        // Narrow rather than cast: a stray data-value in the markup must not
        // reach switchDataSource as a venue.
        if (!(0, venues_1.isDataSource)(newSource))
            return;
        await (0, data_source_service_1.switchDataSource)(newSource, prefixSelector, suffixSelector);
        setKalshiSuffixVisible((0, capabilities_1.usesSeriesSelector)(newSource));
        updateRemoveBtnsVisibility();
        // Load markets with the newly selected category
        const selectedCategory = (0, capabilities_1.usesSeriesSelector)(newSource)
            ? getKalshiCombinedCategory(prefixSelector, suffixSelector)
            : prefixSelector?.value;
        if (selectedCategory) {
            loadEvents(selectedCategory);
        }
    });
    // Settings button
    if (settingsBtn) {
        settingsBtn.addEventListener("click", () => {
            const state = (0, state_1.getState)();
            (0, ui_1.populateSettingsForm)(state.tradingSettings);
            (0, ui_1.showSettingsModal)();
        });
    }
    // Refresh button — no-arg load re-derives the category from the selectors
    if (refreshBtn) {
        refreshBtn.addEventListener("click", () => {
            loadEvents();
        });
    }
    if (openPairBuilderBtn) {
        openPairBuilderBtn.addEventListener("click", () => {
            (0, navigation_service_1.navigateToPairBuilder)();
        });
    }
    if (openMonitorStatusBtn) {
        openMonitorStatusBtn.addEventListener("click", () => {
            (0, navigation_service_1.navigateToMonitorStatus)();
        });
    }
    if (openPnlBtn) {
        openPnlBtn.addEventListener("click", () => {
            (0, navigation_service_1.navigateToPnl)();
        });
    }
    if (openWinProbBtn) {
        openWinProbBtn.addEventListener("click", () => {
            (0, navigation_service_1.navigateToWinProb)();
        });
    }
    // Prefix / league / sport selector
    if (prefixSelector) {
        prefixSelector.dataset.lastValue = prefixSelector.value;
        prefixSelector.addEventListener("change", () => {
            const state = (0, state_1.getState)();
            if ((0, capabilities_1.usesSeriesSelector)(state.dataSource)) {
                if (prefixSelector.value === data_source_service_1.CUSTOM_KALSHI_PREFIX_VALUE) {
                    const resolved = resolveCustomPrefix(prefixSelector);
                    if (!resolved) {
                        updateRemoveBtnsVisibility();
                        return;
                    }
                }
                else {
                    prefixSelector.dataset.lastValue = prefixSelector.value;
                }
                // League changed → refresh the suffix set (baseball offers F5,
                // court sports offer quarters/halves), keeping the current
                // market type if it still applies to the new league.
                (0, data_source_service_1.populateKalshiSuffixes)(suffixSelector, prefixSelector.value, {
                    preserveSelection: true,
                });
                updateRemoveBtnsVisibility();
                const combined = getKalshiCombinedCategory(prefixSelector, suffixSelector);
                if (combined)
                    loadEvents(combined);
                return;
            }
            // Polymarket venues: the prefix selector holds the sport directly.
            prefixSelector.dataset.lastValue = prefixSelector.value;
            updateRemoveBtnsVisibility();
            loadEvents(prefixSelector.value);
        });
    }
    // Suffix (market-type) selector — Kalshi only
    if (suffixSelector) {
        suffixSelector.dataset.lastValue = suffixSelector.value;
        suffixSelector.addEventListener("change", () => {
            if (!(0, capabilities_1.usesSeriesSelector)((0, state_1.getState)().dataSource))
                return;
            if (suffixSelector.value === data_source_service_1.CUSTOM_KALSHI_SUFFIX_VALUE) {
                const resolved = resolveCustomSuffix(suffixSelector);
                updateRemoveBtnsVisibility();
                if (!resolved)
                    return;
            }
            else {
                suffixSelector.dataset.lastValue = suffixSelector.value;
                updateRemoveBtnsVisibility();
            }
            const combined = getKalshiCombinedCategory(prefixSelector, suffixSelector);
            if (combined)
                loadEvents(combined);
        });
    }
    // Favorite button — stores the prefix/suffix pair for Kalshi, the sport otherwise
    if (favoriteBtn && prefixSelector) {
        favoriteBtn.addEventListener("click", () => {
            const state = (0, state_1.getState)();
            if ((0, capabilities_1.usesSeriesSelector)(state.dataSource)) {
                const prefix = prefixSelector.value;
                const suffix = suffixSelector?.value;
                if (prefix &&
                    suffix &&
                    prefix !== data_source_service_1.CUSTOM_KALSHI_PREFIX_VALUE &&
                    suffix !== data_source_service_1.CUSTOM_KALSHI_SUFFIX_VALUE) {
                    (0, state_1.saveKalshiSeriesFavorite)(prefix, suffix);
                    flashFavoriteSaved();
                }
                return;
            }
            const currentValue = prefixSelector.value;
            if (currentValue) {
                (0, state_1.saveFavoriteLeague)(state.dataSource, currentValue);
                flashFavoriteSaved();
            }
        });
    }
    // Remove custom prefix button
    if (removePrefixBtn && prefixSelector) {
        removePrefixBtn.addEventListener("click", () => {
            const selectedOption = prefixSelector.selectedOptions[0];
            if (selectedOption?.dataset.customValue === "true" &&
                prefixSelector.value) {
                (0, state_1.removeCustomPrefix)(prefixSelector.value);
                prefixSelector.removeChild(selectedOption);
                prefixSelector.value = prefixSelector.options[0]?.value || "";
                prefixSelector.dataset.lastValue = prefixSelector.value;
                // The fallback league may use a different suffix set.
                (0, data_source_service_1.populateKalshiSuffixes)(suffixSelector, prefixSelector.value, {
                    preserveSelection: true,
                });
                updateRemoveBtnsVisibility();
                const combined = getKalshiCombinedCategory(prefixSelector, suffixSelector);
                if (combined)
                    loadEvents(combined);
            }
        });
    }
    // Remove custom suffix button
    if (removeSuffixBtn && suffixSelector) {
        removeSuffixBtn.addEventListener("click", () => {
            const selectedOption = suffixSelector.selectedOptions[0];
            if (selectedOption?.dataset.customValue === "true" &&
                suffixSelector.value) {
                (0, state_1.removeCustomSuffix)(suffixSelector.value);
                suffixSelector.removeChild(selectedOption);
                suffixSelector.value = const_1.DEFAULT_KALSHI_SUFFIX;
                suffixSelector.dataset.lastValue = suffixSelector.value;
                updateRemoveBtnsVisibility();
                const combined = getKalshiCombinedCategory(prefixSelector, suffixSelector);
                if (combined)
                    loadEvents(combined);
            }
        });
    }
    updateRemoveBtnsVisibility();
    // Event search
    if (eventSearch) {
        eventSearch.addEventListener("input", (e) => {
            const searchTerm = e.target.value.toLowerCase();
            const state = (0, state_1.getState)();
            const results = (0, markets_1.searchDisplayEvents)(searchTerm, undefined, state.dataSource);
            (0, ui_1.renderEnhancedEventsList)(results);
        });
    }
    // Load markets on authentication based on current data source
    if (currentState.isAuthenticated && !usesFlatLeagueList) {
        console.log("[setupEventsPage] Kalshi authenticated, loading markets with timeout");
        // For Kalshi, the selectors are already populated; load the combined ticker.
        // Note: Polymarket market loading is handled above after sports fetch completes
        setTimeout(() => loadEvents(), 100);
    }
    else {
        console.log("[setupEventsPage] Skipping initial market load:", {
            isAuthenticated: currentState.isAuthenticated,
            usesFlatLeagueList,
            reason: usesFlatLeagueList
                ? "Non-Kalshi source handled above"
                : "Not authenticated",
        });
    }
    console.log("[setupEventsPage] Events page setup complete");
    // Event item click handler
    document.addEventListener("click", (e) => {
        const target = e.target;
        if (target.closest(".event-item")) {
            const eventItem = target.closest(".event-item");
            const eventId = eventItem.dataset.eventId;
            if (eventId) {
                (0, market_selection_service_1.selectEvent)(eventId);
            }
        }
    });
}
